import { use, useCallback } from "react";

import { UserContext } from "../providers/user-context";
import { TranscriptRevisions } from "../types/schemas";
import { parseError } from "../utils/parse-error";

type GetTranscriptRevisions = (
  options: {
    transcriptId: string;
    start: number;
    end?: number;
  },
  signal?: AbortSignal,
) => Promise<TranscriptRevisions>;

/**
 * Retrieve all the revisions between start and end (inclusive).
 * If end is unspecified all revisions from start to the latest will be returned.
 */
export function useGetTranscriptRevisions(): GetTranscriptRevisions {
  const { user } = use(UserContext);

  const getTranscriptRevisions = useCallback<GetTranscriptRevisions>(
    async (options, signal) => {
      const { transcriptId, start, end } = options;
      const searchParams = new URLSearchParams({ start_version: `${start}` });
      if (end !== undefined) searchParams.append("end_version", `${end}`);

      const response = await fetch(
        `/app/v2/revisions/${transcriptId}?${searchParams}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
          signal,
        },
      );

      if (!response.ok) throw parseError(response.status);
      const json = await response.json();

      return json;
    },
    [user],
  );

  return getTranscriptRevisions;
}
